import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Users, Loader2, Phone, Mail, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';

export default function AdminRMs() {
  const [rms, setRMs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', department: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchRMs(); }, []);

  const fetchRMs = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getRMs();
      setRMs(res.data || []);
    } catch { toast.error('Failed to load RMs'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.phone && !form.email) { toast.error('Phone or email is required'); return; }
    if (!editId && !form.password) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await adminAPI.updateRM(editId, payload);
        toast.success('RM updated');
      } else {
        await adminAPI.createRM(form);
        toast.success('RM created');
      }
      resetForm();
      fetchRMs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleEdit = (rm) => {
    setEditId(rm.id);
    setForm({ name: rm.name, phone: rm.phone || '', email: rm.email || '', password: '', department: rm.department || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this RM?')) return;
    try { await adminAPI.deleteRM(id); toast.success('RM deleted'); fetchRMs(); }
    catch { toast.error('Failed to delete'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm({ name: '', phone: '', email: '', password: '', department: '' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 data-testid="rms-title" className="text-xl font-extrabold text-[#1a2529] tracking-tight">Reporting Managers</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">{rms.length} RM{rms.length !== 1 ? 's' : ''}</p>
          </div>
          <Button data-testid="add-rm-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#026536] hover:bg-[#026536]/90 gap-1.5 font-semibold">
            <Plus size={16} /> Add RM
          </Button>
        </div>

        {showForm && (
          <div data-testid="rm-form" className="bg-white rounded-xl p-5 border border-border/40 shadow-sm animate-slide-up">
            <h3 className="font-bold text-sm text-[#1a2529] mb-4">{editId ? 'Edit RM' : 'New RM'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Full Name *</Label>
                <Input data-testid="rm-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Must match reporting_manager field" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Phone</Label>
                <Input data-testid="rm-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="10-digit number" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Email</Label>
                <Input data-testid="rm-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">{editId ? 'New Password (leave empty to keep)' : 'Password *'}</Label>
                <Input data-testid="rm-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 8 chars, upper+lower+digit" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Department</Label>
                <Input data-testid="rm-dept-input" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g., Operations" className="h-10 mt-1" />
              </div>
            </div>
            <p className="text-[10px] text-[#1a2529]/40 mb-3">The RM's name must exactly match the "Reporting Manager" field on their employees' records.</p>
            <div className="flex gap-2">
              <Button data-testid="save-rm-btn" onClick={handleSave} disabled={saving} className="bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-1.5">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {editId ? 'Update' : 'Create'}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" /></div>
        ) : rms.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-border/40">
            <Users className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-semibold text-[#1a2529]/50">No Reporting Managers</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">Create RMs to enable middle-level approval workflows</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rms.map(rm => (
              <div key={rm.id} data-testid={`rm-card-${rm.id}`} className="bg-white rounded-xl p-4 border border-border/40 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-[#1a2529] text-sm">{rm.name}</p>
                    {rm.department && <p className="text-[11px] text-[#1a2529]/40">{rm.department}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-rm-${rm.id}`} onClick={() => handleEdit(rm)} className="h-8 w-8 rounded-lg hover:bg-[#f4f4f5] flex items-center justify-center transition-colors">
                      <Pencil size={14} className="text-[#1a2529]/40" />
                    </button>
                    <button data-testid={`delete-rm-${rm.id}`} onClick={() => handleDelete(rm.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  {rm.phone && <div className="flex items-center gap-1.5 text-[11px] text-[#1a2529]/50"><Phone size={11} />{rm.phone}</div>}
                  {rm.email && <div className="flex items-center gap-1.5 text-[11px] text-[#1a2529]/50"><Mail size={11} />{rm.email}</div>}
                </div>
                <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-[10px]">
                  {rm.employee_count || 0} employees
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
